import { Injectable } from '@angular/core';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { UserPreferencesService } from 'app/shared/services-api-central/user-preferences/user-preferences.service';
import moment from 'moment';
import { defineLocale } from 'ngx-bootstrap/chronos';
import * as locales from 'ngx-bootstrap/locale';
import type { BsLocaleService } from 'ngx-bootstrap/datepicker';
import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import localeEs from '@angular/common/locales/es';
import localeJa from '@angular/common/locales/ja';
import localeFr from '@angular/common/locales/fr';
import localeIt from '@angular/common/locales/it';
import localePt from '@angular/common/locales/pt';
import localeZh from '@angular/common/locales/zh';
import 'moment/locale/zh-tw';
import 'moment/locale/de';
import 'moment/locale/es';
import 'moment/locale/fr';
import 'moment/locale/it';
import 'moment/locale/ja';
import 'moment/locale/pt';
@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private locale = new BehaviorSubject('');

  public static readonly languages = ['en', 'de', 'es', 'fr', 'it', 'pt', 'ja', 'zh'];


  private static locales = {
    en: ['en-gb', localeEn, locales['enGbLocale']],
    de: ['de', localeDe, locales['deLocale']],
    es: ['es', localeEs, locales['esLocale']],
    fr: ['fr', localeFr, locales['frLocale']],
    it: ['it', localeIt, locales['itLocale']],
    pt: ['pt-br', localePt, locales['ptBrLocale']],
    ja: ['ja', localeJa, locales['jaLocale']],
    zh: ['zh-tw', localeZh, locales['zhCnLocale']],
  }


  constructor(private translateService: TranslateService, private userPreferencesService: UserPreferencesService) {}

  public initLanguage(): void {
    this.translateService.addLangs(LanguageService.languages); // Add new langs to the list
    this.translateService.setDefaultLang('en'); // Sets the default language to use as a fallback

    this.userPreferencesService
      .getUserPreferencesLoaded()
      .pipe(filter((loaded) => loaded === true))
      .subscribe(() => {
        this.getStoredLanguageHandler();
      });

    this.subscribeToLanguageChange();
  }

  private getStoredLanguageHandler(): void {
    const storedLang = this.userPreferencesService.language;

    if (storedLang !== null) {
      this.setCurrentLanguage(storedLang);
      moment.locale(storedLang);
    } else {
      const browserLang = this.translateService.getBrowserLang();
      const languages = this.translateService.getLangs();
      const locale = languages.includes(browserLang) ? browserLang : 'EN';

      this.handleSelectedChanged(locale.toUpperCase());
      moment.locale(locale.toLowerCase());
    }
  }

  private setCurrentLanguage(language: string): void {
    this.translateService.use(language.toLocaleLowerCase());
    if (language.toLocaleLowerCase() !== this.userPreferencesService.language) {
      this.userPreferencesService.language = language.toLocaleLowerCase();
    }
    this.locale.next(language.toUpperCase());
  }

  public handleSelectedChanged(language: string): void {
    this.translateService.use(language.toLocaleLowerCase());
  }

  private subscribeToLanguageChange(): void {
    this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      this.locale.next(event.lang.toUpperCase());
    });
  }

  public updateCalendars(language: string, localeService: BsLocaleService): void {
    let lang = LanguageService.locales[language];
    defineLocale(lang[0], lang[2]);
    localeService.use(lang[0]);
    moment.locale(language);
  }


  public static registerLocales(): void {
    for (const locale in LanguageService.locales) {
      registerLocaleData(LanguageService.locales[locale][1], locale);
      const [locale1, _, local3] = LanguageService.locales[locale];
      defineLocale(locale1, local3);
      if (locale === 'pt' || locale === 'zh') defineLocale(locale, local3);
    }

    // Register 'zh' as an alias to 'zh-tw' for moment
    const zhTwData = moment.localeData('zh-tw');
    if (zhTwData) {
      moment.defineLocale('zh', (zhTwData as any)._config);
    }
  }

  public getLocale(): BehaviorSubject<string> {
    return this.locale;
  }
}
